import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { Deployment } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { useListDeployments } from '@sunstone/api-client/hooks';
import {
  Badge,
  Button,
  DataTable,
  PillFilter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sunstone/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { type Blip, BlipsStrip } from '@/features/health/HealthBlips';
import { useDebounce } from '@/hooks/useDebounce';
import { useListState } from '@/hooks/useListState';
import {
  deploymentModelKey,
  deploymentStatusFromKey,
  deploymentStatusKey,
  deploymentTypeFromKey,
  deploymentTypeKey,
} from './deploymentFormatters';

const PAGE_SIZE = 100;

const CLOUD_BADGES: Record<string, { label: string; className: string }> = {
  DEPLOYMENT_TYPE_CLOUD_AWS: {
    label: 'AWS',
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  },
  DEPLOYMENT_TYPE_CLOUD_GCP: {
    label: 'GCP',
    className: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  },
  DEPLOYMENT_TYPE_CLOUD_AZURE: {
    label: 'Azure',
    className: 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
  },
  DEPLOYMENT_TYPE_ON_PREM: {
    label: 'On-Prem',
    className: 'bg-lime-50 text-lime-700 dark:bg-lime-950 dark:text-lime-300',
  },
};

const MODEL_LABELS: Record<string, string> = {
  DEPLOYMENT_MODEL_HYBRID: 'Hybrid',
  DEPLOYMENT_MODEL_SAAS: 'SaaS',
  DEPLOYMENT_MODEL_AIRGAPPED: 'Airgapped',
};

const CLOUD_FILTER_OPTIONS = [
  { value: 'DEPLOYMENT_TYPE_CLOUD_AWS', label: 'AWS' },
  { value: 'DEPLOYMENT_TYPE_CLOUD_GCP', label: 'GCP' },
  { value: 'DEPLOYMENT_TYPE_CLOUD_AZURE', label: 'Azure' },
  { value: 'DEPLOYMENT_TYPE_ON_PREM', label: 'On-Prem' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'DEPLOYMENT_STATUS_CREATED', label: 'Created' },
  { value: 'DEPLOYMENT_STATUS_AWAITING_BOOTSTRAP', label: 'Awaiting Bootstrap' },
  { value: 'DEPLOYMENT_STATUS_BOOTSTRAP_FAILED', label: 'Bootstrap Failed' },
  { value: 'DEPLOYMENT_STATUS_PROVISIONING', label: 'Provisioning' },
  { value: 'DEPLOYMENT_STATUS_ACTIVE', label: 'Active' },
  { value: 'DEPLOYMENT_STATUS_MAINTENANCE', label: 'Maintenance' },
  { value: 'DEPLOYMENT_STATUS_INACTIVE', label: 'Inactive' },
  { value: 'DEPLOYMENT_STATUS_DEPROVISIONING', label: 'Deprovisioning' },
  { value: 'DEPLOYMENT_STATUS_MANAGEMENT_BLOCKED', label: 'Management Blocked' },
];

const ALL = '__all__';

interface DeploymentRow extends Deployment {
  _tenantName: string;
}

interface DeploymentListProps {
  tenantId?: string;
  parentTenantId?: string;
  urlParams?: boolean;
  healthBlipsByEntityId?: Record<string, Blip[]>;
  healthLoading?: boolean;
}

export function DeploymentList({
  tenantId,
  parentTenantId,
  urlParams = false,
  healthBlipsByEntityId,
  healthLoading,
}: DeploymentListProps) {
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { scope } = useAuthorization();

  const { page, search, setPage, setSearch, getFilter, setFilter } = useListState(urlParams);
  const cloudFilter = getFilter('cloud');
  const statusFilter = getFilter('status');

  const debouncedSearch = useDebounce(search);

  const showsTenantColumn = !tenantId && scope !== 'tenant';

  const handleCloudFilterChange = useCallback(
    (v: string | null) => setFilter('cloud', v),
    [setFilter],
  );

  const handleStatusFilterChange = useCallback(
    (v: string | null) => setFilter('status', v),
    [setFilter],
  );

  const handleSearchChange = useCallback((v: string) => setSearch(v), [setSearch]);

  const { data, isLoading, error } = useListDeployments(token, tenantId, parentTenantId, {
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
    deploymentTypeFilter: deploymentTypeFromKey(cloudFilter),
    statusFilter: deploymentStatusFromKey(statusFilter),
  });

  const allDeployments = data?.deployments ?? [];
  const totalCount = data?.totalCount ?? 0;

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        Failed to load deployments.
      </div>
    );
  }

  const rows: DeploymentRow[] = allDeployments.map((d: Deployment) => ({
    ...d,
    _tenantName: d.tenantName,
  }));

  const columns: ColumnDef<DeploymentRow>[] = [
    {
      id: 'displayName',
      accessorKey: 'displayName',
      header: 'Name',
      meta: { className: 'max-w-[220px]' },
      cell: ({ row }) => (
        <button
          type="button"
          className="block w-full min-w-0 cursor-pointer text-left"
          onClick={() =>
            navigate(`/tenants/${row.original.tenantId}/deployments/${row.original.id}`)
          }
        >
          <span className="block truncate font-medium text-foreground hover:text-primary transition-colors">
            {row.original.displayName}
          </span>
        </button>
      ),
    },
    {
      id: 'slug',
      accessorKey: 'slug',
      header: 'URL Name',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground font-mono hidden sm:block">
          {row.original.slug || '—'}
        </span>
      ),
    },
    {
      id: 'cloud',
      header: 'Cloud',
      cell: ({ row }) => {
        const key = deploymentTypeKey(row.original.deploymentType);
        const cfg = CLOUD_BADGES[key] ?? {
          label: key,
          className: 'bg-slate-50 text-slate-700 dark:bg-slate-900 dark:text-slate-300',
        };
        return <Badge className={`text-xs ${cfg.className}`}>{cfg.label}</Badge>;
      },
    },
    {
      id: 'region',
      accessorKey: 'region',
      header: 'Region',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground font-mono hidden sm:block">
          {row.original.region || '—'}
        </span>
      ),
    },
    ...(showsTenantColumn
      ? [
          {
            id: 'tenant',
            header: 'Customer',
            cell: ({ row }: { row: { original: DeploymentRow } }) => (
              <span className="text-sm text-muted-foreground hidden lg:block">
                {row.original._tenantName}
              </span>
            ),
          } as ColumnDef<DeploymentRow>,
        ]
      : []),
    {
      id: 'model',
      header: 'Model',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground hidden lg:block">
          {MODEL_LABELS[deploymentModelKey(row.original.deploymentModel)] ??
            deploymentModelKey(row.original.deploymentModel)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={deploymentStatusKey(row.original.status)} />,
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground hidden md:block">
          {row.original.createdAt
            ? timestampDate(row.original.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : '—'}
        </span>
      ),
    },
    ...(healthBlipsByEntityId || healthLoading
      ? [
          {
            id: 'health',
            header: 'Health (24h)',
            cell: ({ row }: { row: { original: DeploymentRow } }) => (
              <div className="w-40">
                <BlipsStrip
                  blips={healthBlipsByEntityId?.[row.original.id] ?? []}
                  isLoading={healthLoading}
                />
              </div>
            ),
          } as ColumnDef<DeploymentRow>,
        ]
      : []),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground/40 hover:text-muted-foreground"
          onClick={() =>
            navigate(`/tenants/${row.original.tenantId}/deployments/${row.original.id}`)
          }
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  const filterSlot = (
    <>
      <PillFilter
        label="Cloud"
        value={cloudFilter}
        onChange={handleCloudFilterChange}
        options={CLOUD_FILTER_OPTIONS}
      />
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground font-medium">Status:</span>
        <Select
          value={statusFilter ?? ALL}
          onValueChange={(v) => handleStatusFilterChange(v === ALL ? null : v)}
        >
          <SelectTrigger className="h-7 w-[160px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any status</SelectItem>
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      showSearch
      searchPlaceholder="Search"
      searchValue={search}
      onSearchChange={handleSearchChange}
      filterSlot={filterSlot}
      isLoading={isLoading}
      totalCount={totalCount}
      currentPage={page}
      onPageChange={setPage}
      pageSize={PAGE_SIZE}
      onRowClick={(row) => navigate(`/tenants/${row.tenantId}/deployments/${row.id}`)}
    />
  );
}
